import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import { createPortal } from "react-dom";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { CoverageBar } from "@/components/ui/CoverageBar";
import { Input } from "@/components/ui/Input";
import { Pill } from "@/components/ui/Pill";
import { Select } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/lib/api/client";
import { scrollToFormError } from "@/lib/scroll-to-form-error";
import { usePdfPasswordPrompt } from "@/hooks/usePdfPasswordPrompt";
import {
  requiredPersonalDocKeys,
  shareholderRequiresPersonalKyc,
  UBO_SHAREHOLDING_THRESHOLD,
  validatePersonalKycForm,
  validateShareholderForm,
} from "@/lib/partner-validations";
import {
  buildPersonalChecklist,
  deleteApplicationDocumentFile,
  listApplicationDocuments,
  listDocumentMappings,
  uploadApplicationDocument,
  type DocumentMapping,
  type ApplicationDocument,
} from "@/services/documents";
import {
  createPersonalKyc,
  createShareholder,
  deleteShareholder,
  getKycReference,
  listShareholders,
  updatePersonalKyc,
  updateShareholder,
  type IdNameOption,
  type PersonalKyc,
  type Shareholder,
  type ShareholderType,
} from "@/services/kyc";
import type { DocumentChecklistItem } from "@/types/application";

type ShareholdersPanelProps = {
  applicationUuid: string;
  /** True after credit is approved — shareholder and shareholder KYC mutations are blocked. */
  locked?: boolean;
  onChanged?: () => Promise<void> | void;
};

type ShareholderForm = {
  name: string;
  shareholder_type: ShareholderType;
  shareholding_percentage: string;
  is_business_owner: boolean;
};

type PersonalForm = {
  name: string;
  nationality: string;
  passport_no: string;
  is_uae_resident: string;
  identity: string;
};

const emptyShareholderForm = (): ShareholderForm => ({
  name: "",
  shareholder_type: "INDIVIDUAL",
  shareholding_percentage: "",
  is_business_owner: false,
});

function personalFormFromKyc(kyc: PersonalKyc | null, fallbackName: string): PersonalForm {
  return {
    name: kyc?.name || fallbackName || "",
    nationality: kyc?.nationality != null ? String(kyc.nationality) : "",
    passport_no: kyc?.passport_no || "",
    is_uae_resident: kyc ? (kyc.is_uae_resident ? "true" : "false") : "",
    identity: kyc?.identity || "",
  };
}

function kycStatusTone(sh: Shareholder): "success" | "warning" | "muted" {
  if (sh.personal_kyc) return sh.personal_kyc.status === "verified" ? "success" : "warning";
  return needsPersonalKyc(sh) ? "warning" : "muted";
}

function kycStatusLabel(sh: Shareholder): string {
  if (sh.personal_kyc) return sh.personal_kyc.status_display;
  return needsPersonalKyc(sh) ? "KYC not added" : "KYC not required";
}

function needsPersonalKyc(sh: Shareholder): boolean {
  return (
    Boolean(sh.kyc_required) ||
    shareholderRequiresPersonalKyc({
      is_business_owner: sh.is_business_owner,
      shareholding_percentage: sh.shareholding_percentage,
    })
  );
}

function poolTotal(rows: Shareholder[]): number {
  return rows.reduce(
    (sum, sh) =>
      sum + (sh.shareholding_percentage != null ? Number(sh.shareholding_percentage) : 0),
    0,
  );
}

/** Splits a flat shareholder list into the borrower company's top-level
 * holders and, for each top-level BUSINESS holder, its own individual
 * shareholders (one level of nesting; the backend only allows mapping an
 * INDIVIDUAL row under a top-level BUSINESS row). */
function groupShareholders(shareholders: Shareholder[]): {
  topLevel: Shareholder[];
  childrenByParent: Map<string, Shareholder[]>;
} {
  const topLevel: Shareholder[] = [];
  const childrenByParent = new Map<string, Shareholder[]>();
  for (const sh of shareholders) {
    if (sh.business_shareholder) {
      const list = childrenByParent.get(sh.business_shareholder) ?? [];
      list.push(sh);
      childrenByParent.set(sh.business_shareholder, list);
    } else {
      topLevel.push(sh);
    }
  }
  return { topLevel, childrenByParent };
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={
        open ? "rotate-180 text-muted transition-transform" : "text-muted transition-transform"
      }
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

type ShareholderCardProps = {
  sh: Shareholder;
  indent: boolean;
  locked: boolean;
  nationalities: IdNameOption[];
  expanded: boolean;
  editingKyc: boolean;
  personalForm: PersonalForm;
  setPersonalForm: (updater: (prev: PersonalForm) => PersonalForm) => void;
  kycError: string | null;
  kycFieldErrors: Record<string, string>;
  kycSaving: boolean;
  pendingFiles: Record<string, File[]>;
  expandedDocKeys: Record<string, boolean>;
  editChecklist: DocumentChecklistItem[];
  viewChecklist: DocumentChecklistItem[];
  onEdit: () => void;
  onDelete: () => void;
  onToggleExpand: () => void;
  onOpenKycEdit: () => void;
  onCloseKycEdit: () => void;
  onSavePersonalKyc: (event: FormEvent) => void;
  onToggleDocFiles: (documentKey: string) => void;
  onOpenDocPicker: (documentKey: string) => void;
  onRemovePendingFile: (documentKey: string, index: number) => void;
  onDeleteExistingFile: (item: DocumentChecklistItem, fileUuid: string) => void;
};

function ShareholderCard({
  sh,
  indent,
  locked,
  nationalities,
  expanded,
  editingKyc,
  personalForm,
  setPersonalForm,
  kycError,
  kycFieldErrors,
  kycSaving,
  pendingFiles,
  expandedDocKeys,
  editChecklist,
  viewChecklist,
  onEdit,
  onDelete,
  onToggleExpand,
  onOpenKycEdit,
  onCloseKycEdit,
  onSavePersonalKyc,
  onToggleDocFiles,
  onOpenDocPicker,
  onRemovePendingFile,
  onDeleteExistingFile,
}: ShareholderCardProps) {
  const verified = sh.personal_kyc?.status === "verified";
  const canEditKyc = !locked && (!sh.personal_kyc || sh.personal_kyc.can_edit);
  const requiresKyc = needsPersonalKyc(sh);
  const holdingLabel =
    sh.shareholding_percentage != null ? `${Number(sh.shareholding_percentage).toFixed(2)}%` : "—";

  const header = (
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <p className="font-medium text-ink">{sh.name}</p>
        <p className="mt-0.5 text-xs text-muted">
          {sh.shareholder_type_display} · {holdingLabel}
          {sh.is_business_owner ? " · Beneficial owner" : ""}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Pill tone={kycStatusTone(sh)}>{kycStatusLabel(sh)}</Pill>
        {requiresKyc ? <ChevronIcon open={expanded} /> : null}
      </div>
    </div>
  );

  return (
    <div
      // A nested card reads as "inside" its parent's card via a left accent
      // border instead of its own full bordered box.
      className={
        indent
          ? "border-l-2 border-brand/30 py-2 pl-3"
          : "rounded-xl border border-line bg-surface p-4"
      }
    >
      {requiresKyc ? (
        <button type="button" onClick={onToggleExpand} className="w-full text-left">
          {header}
        </button>
      ) : (
        header
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {!locked && !verified ? (
          <Button variant="secondary" size="sm" onClick={onEdit}>
            Edit shareholder
          </Button>
        ) : null}
        {requiresKyc && canEditKyc ? (
          <Button size="sm" onClick={onOpenKycEdit}>
            {sh.personal_kyc ? "Edit KYC" : "+ Add KYC"}
          </Button>
        ) : null}
        {!locked && !verified ? (
          <Button variant="ghost" size="sm" onClick={onDelete}>
            Delete
          </Button>
        ) : null}
      </div>

      {expanded && requiresKyc ? (
        <div className="mt-3 border-t border-line pt-3">
          <div className="rounded-xl border border-line bg-surface-raised p-4">
            <h3 className="mb-4 text-sm font-semibold text-ink">Personal KYC</h3>
            {sh.personal_kyc ? (
              <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted">Name</p>
                  <p className="mt-1 text-ink">{sh.personal_kyc.name || "—"}</p>
                </div>
                {sh.shareholder_type === "BUSINESS" ? (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted">Company ID</p>
                    <p className="mt-1 text-ink">{sh.personal_kyc.identity || "—"}</p>
                  </div>
                ) : (
                  <>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted">Nationality</p>
                      <p className="mt-1 text-ink">{sh.personal_kyc.nationality_name || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted">Passport number</p>
                      <p className="mt-1 text-ink">{sh.personal_kyc.passport_no || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted">UAE resident</p>
                      <p className="mt-1 text-ink">
                        {sh.personal_kyc.is_uae_resident ? "Yes" : "No"}
                      </p>
                    </div>
                  </>
                )}
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted">Status</p>
                  <p className="mt-1 text-ink">{sh.personal_kyc.status_display}</p>
                </div>
              </div>
            ) : (
              <p className="mb-5 text-sm text-muted">
                {locked
                  ? "No personal KYC on file."
                  : "No personal KYC yet. Click “+ Add KYC” to start."}
              </p>
            )}

            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">
              Documents
            </h4>
            {viewChecklist.length === 0 ? (
              <p className="text-sm text-muted">No documents.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                      <th className="px-2 py-2 font-medium">Document name</th>
                      <th className="px-2 py-2 font-medium">Files</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewChecklist.map((item) => {
                      const files = item.files || [];
                      const docExpanded = Boolean(expandedDocKeys[item.document_key]);
                      return (
                        <Fragment key={item.document_key}>
                          <tr className="border-b border-line align-top">
                            <td className="px-2 py-3">
                              <p className="font-medium text-ink">
                                {item.label}
                                {item.required ? <span className="text-danger"> *</span> : null}
                              </p>
                              <p className="mt-0.5 text-xs text-muted">Multiple uploads allowed</p>
                            </td>
                            <td className="px-2 py-3">
                              {files.length > 0 ? (
                                <button
                                  type="button"
                                  className="text-sm font-medium text-brand hover:underline"
                                  onClick={() => onToggleDocFiles(item.document_key)}
                                >
                                  {docExpanded ? "Hide files" : `Show files (${files.length})`}
                                </button>
                              ) : (
                                <span className="text-muted">No files</span>
                              )}
                            </td>
                          </tr>
                          {docExpanded && files.length > 0 ? (
                            <tr className="border-b border-line bg-surface">
                              <td colSpan={2} className="px-2 py-3">
                                <ul className="space-y-2 rounded-xl border border-line p-3">
                                  {files.map((file) => (
                                    <li key={file.uuid} className="min-w-0">
                                      {file.file_url ? (
                                        <a
                                          href={file.file_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="break-all font-medium text-brand hover:underline"
                                        >
                                          {file.file_name}
                                        </a>
                                      ) : (
                                        <span className="break-all text-ink">{file.file_name}</span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {editingKyc && !locked
        ? createPortal(
            <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40">
              <div
                className="flex min-h-full items-center justify-center px-4 py-6"
                onClick={(e) => {
                  if (e.target === e.currentTarget && !kycSaving) onCloseKycEdit();
                }}
              >
                <form
                  onSubmit={onSavePersonalKyc}
                  role="dialog"
                  aria-modal="true"
                  className="w-full max-w-4xl rounded-2xl border border-line bg-surface-raised p-6 shadow-xl"
                  noValidate
                >
                  <h3 className="mb-1 text-sm font-semibold text-ink">
                    {sh.personal_kyc ? "Edit KYC" : "Add KYC"}
                  </h3>
                  <p className="mb-4 text-xs text-muted">
                    Save updates personal details and uploads any selected documents.
                  </p>

                  {kycError ? <Alert className="mb-4">{kycError}</Alert> : null}

                  <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">
                    Details
                  </h4>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <Input
                      label="Name *"
                      name="kyc_name"
                      value={personalForm.name}
                      error={kycFieldErrors.name}
                      onChange={(e) =>
                        setPersonalForm((prev) => ({ ...prev, name: e.target.value }))
                      }
                    />
                    {sh.shareholder_type === "BUSINESS" ? (
                      <Input
                        label="Company ID *"
                        name="identity"
                        value={personalForm.identity}
                        error={kycFieldErrors.identity}
                        onChange={(e) =>
                          setPersonalForm((prev) => ({ ...prev, identity: e.target.value }))
                        }
                      />
                    ) : (
                      <>
                        <Select
                          label="Nationality *"
                          name="nationality"
                          value={personalForm.nationality}
                          error={kycFieldErrors.nationality}
                          onChange={(e) =>
                            setPersonalForm((prev) => ({ ...prev, nationality: e.target.value }))
                          }
                          options={[
                            { value: "", label: "Select nationality" },
                            ...nationalities.map((n) => ({
                              value: String(n.id),
                              label: n.name,
                            })),
                          ]}
                        />
                        <Select
                          label="UAE Resident *"
                          name="is_uae_resident"
                          value={personalForm.is_uae_resident}
                          error={kycFieldErrors.is_uae_resident}
                          onChange={(e) =>
                            setPersonalForm((prev) => ({
                              ...prev,
                              is_uae_resident: e.target.value,
                            }))
                          }
                          options={[
                            { value: "", label: "Select" },
                            { value: "true", label: "Yes" },
                            { value: "false", label: "No" },
                          ]}
                        />
                        <div>
                          <Input
                            label="Passport No"
                            name="passport_no"
                            value={personalForm.passport_no}
                            error={kycFieldErrors.passport_no}
                            onChange={(e) =>
                              setPersonalForm((prev) => ({ ...prev, passport_no: e.target.value }))
                            }
                          />
                          <p className="mt-1 text-xs text-muted">Optional.</p>
                        </div>
                      </>
                    )}
                  </div>

                  <h4 className="mb-3 mt-6 text-xs font-semibold uppercase tracking-wide text-muted">
                    Documents
                  </h4>
                  {editChecklist.length === 0 ? (
                    <p className="text-sm text-muted">
                      No document types configured for personal KYC.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[640px] border-collapse text-left text-sm">
                        <thead>
                          <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                            <th className="px-2 py-2 font-medium">Document name</th>
                            <th className="px-2 py-2 font-medium">Files</th>
                            <th className="px-2 py-2 font-medium">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editChecklist.map((item) => {
                            const existing = item.files || [];
                            const pending = pendingFiles[item.document_key] || [];
                            const totalCount = existing.length + pending.length;
                            const docExpanded = Boolean(expandedDocKeys[item.document_key]);

                            return (
                              <Fragment key={item.document_key}>
                                <tr className="border-b border-line align-top">
                                  <td className="px-2 py-3">
                                    <p className="font-medium text-ink">
                                      {item.label}
                                      {item.required ? (
                                        <span className="text-danger"> *</span>
                                      ) : null}
                                    </p>
                                    <p className="mt-0.5 text-xs text-muted">
                                      Multiple uploads allowed
                                    </p>
                                  </td>
                                  <td className="px-2 py-3">
                                    {totalCount > 0 ? (
                                      <button
                                        type="button"
                                        className="text-sm font-medium text-brand hover:underline"
                                        onClick={() => onToggleDocFiles(item.document_key)}
                                      >
                                        {docExpanded ? "Hide files" : `Show files (${totalCount})`}
                                      </button>
                                    ) : (
                                      <span className="text-muted">No files</span>
                                    )}
                                  </td>
                                  <td className="px-2 py-3">
                                    <Button
                                      type="button"
                                      variant={totalCount ? "secondary" : "primary"}
                                      size="sm"
                                      onClick={() => onOpenDocPicker(item.document_key)}
                                    >
                                      {totalCount ? "Add files" : "Upload"}
                                    </Button>
                                  </td>
                                </tr>
                                {docExpanded && totalCount > 0 ? (
                                  <tr className="border-b border-line bg-surface">
                                    <td colSpan={3} className="px-2 py-3">
                                      <ul className="space-y-2 rounded-xl border border-line p-3">
                                        {existing.map((file) => (
                                          <li
                                            key={file.uuid}
                                            className="flex flex-wrap items-center justify-between gap-2"
                                          >
                                            <div className="min-w-0 flex-1">
                                              {file.file_url ? (
                                                <a
                                                  href={file.file_url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="break-all font-medium text-brand hover:underline"
                                                >
                                                  {file.file_name}
                                                </a>
                                              ) : (
                                                <span className="break-all text-ink">
                                                  {file.file_name}
                                                </span>
                                              )}
                                            </div>
                                            <Button
                                              type="button"
                                              variant="secondary"
                                              size="sm"
                                              onClick={() => onDeleteExistingFile(item, file.uuid)}
                                            >
                                              Delete
                                            </Button>
                                          </li>
                                        ))}
                                        {pending.map((file, index) => (
                                          <li
                                            key={`${item.document_key}-pending-${index}`}
                                            className="flex flex-wrap items-center justify-between gap-2"
                                          >
                                            <div className="min-w-0 flex-1">
                                              <span className="break-all text-ink">
                                                {file.name}
                                              </span>
                                              <span className="ml-2 text-xs text-muted">
                                                (pending save)
                                              </span>
                                            </div>
                                            <Button
                                              type="button"
                                              variant="secondary"
                                              size="sm"
                                              onClick={() =>
                                                onRemovePendingFile(item.document_key, index)
                                              }
                                            >
                                              Delete
                                            </Button>
                                          </li>
                                        ))}
                                      </ul>
                                    </td>
                                  </tr>
                                ) : null}
                              </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="mt-5 flex justify-end gap-2 border-t border-line pt-4">
                    <Button type="button" variant="ghost" size="sm" onClick={onCloseKycEdit}>
                      Cancel
                    </Button>
                    <Button type="submit" size="sm" loading={kycSaving}>
                      Save KYC
                    </Button>
                  </div>
                </form>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export function ShareholdersPanel({
  applicationUuid,
  locked = false,
  onChanged,
}: ShareholdersPanelProps) {
  const toast = useToast();
  const { uploadFileWithPasswordPrompt, renderPasswordModal } = usePdfPasswordPrompt();
  const [shareholders, setShareholders] = useState<Shareholder[]>([]);
  const [nationalities, setNationalities] = useState<IdNameOption[]>([]);
  const [mappings, setMappings] = useState<DocumentMapping[]>([]);
  const [documents, setDocuments] = useState<ApplicationDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingUuid, setEditingUuid] = useState<string | null>(null);
  /** UUID of the top-level BUSINESS shareholder the form's row holds shares
   * in — set when adding/editing an individual shareholder nested under a
   * business shareholder, null for a top-level holder of the borrower. */
  const [formParentUuid, setFormParentUuid] = useState<string | null>(null);
  const [form, setForm] = useState<ShareholderForm>(emptyShareholderForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const [expandedUuid, setExpandedUuid] = useState<string | null>(null);
  const [editingKycUuid, setEditingKycUuid] = useState<string | null>(null);
  const [personalForm, setPersonalForm] = useState<PersonalForm>(personalFormFromKyc(null, ""));
  const [kycSaving, setKycSaving] = useState(false);
  const [kycError, setKycError] = useState<string | null>(null);
  const [kycFieldErrors, setKycFieldErrors] = useState<Record<string, string>>({});
  const [pendingFiles, setPendingFiles] = useState<Record<string, File[]>>({});
  const [expandedDocKeys, setExpandedDocKeys] = useState<Record<string, boolean>>({});
  const [activeDocKey, setActiveDocKey] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, reference, docMappings, docs] = await Promise.all([
        listShareholders(applicationUuid),
        getKycReference(),
        listDocumentMappings({ category: "personal_kyc" }),
        listApplicationDocuments(applicationUuid),
      ]);
      setShareholders(rows);
      setNationalities(reference.nationalities || []);
      setMappings(docMappings);
      setDocuments(docs);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to load shareholders");
    } finally {
      setLoading(false);
    }
  }, [applicationUuid]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!locked) return;
    setShowForm(false);
    setEditingUuid(null);
    setFormParentUuid(null);
    setEditingKycUuid(null);
  }, [locked]);

  const checklistFor = useCallback(
    (sh: Shareholder, isUaeResident?: boolean) =>
      buildPersonalChecklist(mappings, documents, {
        shareholderId: sh.id,
        shareholderType: sh.shareholder_type,
        isUaeResident: isUaeResident ?? Boolean(sh.personal_kyc?.is_uae_resident),
      }),
    [mappings, documents],
  );

  const openCreate = (parentUuid: string | null = null) => {
    if (locked) return;
    setEditingUuid(null);
    setFormParentUuid(parentUuid);
    setForm(
      parentUuid
        ? { ...emptyShareholderForm(), shareholder_type: "INDIVIDUAL" }
        : emptyShareholderForm(),
    );
    setShowForm(true);
    setError(null);
    setFieldErrors({});
  };

  const openEdit = (sh: Shareholder) => {
    if (locked) return;
    if (sh.personal_kyc?.status === "verified") return;
    setEditingUuid(sh.uuid);
    setFormParentUuid(sh.business_shareholder ?? null);
    setForm({
      name: sh.name,
      shareholder_type: sh.shareholder_type,
      shareholding_percentage:
        sh.shareholding_percentage != null ? String(sh.shareholding_percentage) : "",
      is_business_owner: sh.is_business_owner,
    });
    setShowForm(true);
    setError(null);
    setFieldErrors({});
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingUuid(null);
    setFormParentUuid(null);
    setForm(emptyShareholderForm());
    setFieldErrors({});
    setError(null);
  };

  const onHoldingChange = (value: string) => {
    const holding = Number(value);
    setForm((prev) => ({
      ...prev,
      shareholding_percentage: value,
      is_business_owner:
        !Number.isNaN(holding) && holding > UBO_SHAREHOLDING_THRESHOLD
          ? true
          : prev.is_business_owner,
    }));
  };

  const onSaveShareholder = async () => {
    if (locked) return;
    // Each company's shareholding (the borrower itself, or a business
    // shareholder's own cap table) is capped at 100% independently —
    // mirrors the backend's per-pool `pool_total()` check.
    const poolSiblings = shareholders.filter(
      (sh) => (sh.business_shareholder ?? null) === formParentUuid && sh.uuid !== editingUuid,
    );
    const otherHoldingsTotal = poolTotal(poolSiblings);
    const nextErrors = validateShareholderForm(form, { otherHoldingsTotal });
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setError("Please fix the highlighted shareholder fields.");
      scrollToFormError();
      return;
    }

    const name = form.name.trim();
    const holding = Number(form.shareholding_percentage);

    setSaving(true);
    setError(null);
    try {
      const payload = {
        name,
        shareholder_type: form.shareholder_type,
        shareholding_percentage: holding,
        is_business_owner: form.is_business_owner,
        ...(formParentUuid ? { business_shareholder: formParentUuid } : {}),
      };
      if (editingUuid) {
        await updateShareholder(editingUuid, payload);
        toast.success("Shareholder saved.");
      } else {
        await createShareholder(applicationUuid, payload);
        toast.success("Shareholder created.");
      }
      closeForm();
      await load();
      await onChanged?.();
    } catch (err) {
      if (err instanceof ApiError) {
        const next: Record<string, string> = {};
        for (const [key, value] of Object.entries(err.fieldErrors)) {
          next[key] = Array.isArray(value) ? value[0] : value;
        }
        setFieldErrors(next);
        const message = err.message || "Unable to save shareholder";
        setError(message);
        toast.error(message);
        scrollToFormError();
      } else {
        const message = "Unable to save shareholder";
        setError(message);
        toast.error(message);
        scrollToFormError();
      }
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (sh: Shareholder) => {
    if (locked) return;
    if (sh.personal_kyc?.status === "verified") return;
    const childCount = shareholders.filter((s) => s.business_shareholder === sh.uuid).length;
    const message =
      childCount > 0
        ? `Delete shareholder “${sh.name}”? Its ${childCount} individual shareholder(s) will be removed too. This cannot be undone.`
        : `Delete shareholder “${sh.name}”?`;
    if (!window.confirm(message)) return;
    setError(null);
    try {
      await deleteShareholder(sh.uuid);
      if (expandedUuid === sh.uuid) setExpandedUuid(null);
      if (editingKycUuid === sh.uuid) setEditingKycUuid(null);
      await load();
      await onChanged?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to delete shareholder");
    }
  };

  const toggleExpand = (uuid: string) => {
    setExpandedUuid((prev) => (prev === uuid ? null : uuid));
    setEditingKycUuid(null);
    setKycError(null);
    setPendingFiles({});
    setExpandedDocKeys({});
  };

  const openKycEdit = (sh: Shareholder) => {
    if (locked) return;
    const pk = sh.personal_kyc;
    if (pk && !pk.can_edit) return;
    setEditingKycUuid(sh.uuid);
    setPersonalForm(personalFormFromKyc(pk, sh.name));
    setKycError(null);
    setKycFieldErrors({});
    setPendingFiles({});
    setExpandedDocKeys({});
    setExpandedUuid(sh.uuid);
  };

  const closeKycEdit = () => {
    setEditingKycUuid(null);
    setKycError(null);
    setKycFieldErrors({});
    setPendingFiles({});
    setExpandedDocKeys({});
    setActiveDocKey(null);
  };

  const toggleDocFiles = (documentKey: string) => {
    setExpandedDocKeys((prev) => ({ ...prev, [documentKey]: !prev[documentKey] }));
  };

  const openDocPicker = (documentKey: string) => {
    if (locked) return;
    setActiveDocKey(documentKey);
    setKycError(null);
    fileInputRef.current?.click();
  };

  const onPendingFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    const key = activeDocKey;
    event.target.value = "";
    setActiveDocKey(null);
    if (!files.length || !key) return;
    setPendingFiles((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []), ...files],
    }));
    setExpandedDocKeys((prev) => ({ ...prev, [key]: true }));
  };

  const removePendingFile = (documentKey: string, index: number) => {
    setPendingFiles((prev) => {
      const nextFiles = (prev[documentKey] || []).filter((_, i) => i !== index);
      const next = { ...prev };
      if (nextFiles.length === 0) delete next[documentKey];
      else next[documentKey] = nextFiles;
      return next;
    });
  };

  const onSavePersonalKyc = async (sh: Shareholder, event: FormEvent) => {
    event.preventDefault();
    if (locked) return;
    const formErrors = validatePersonalKycForm(personalForm, sh.shareholder_type);
    setKycFieldErrors(formErrors);
    if (Object.keys(formErrors).length) {
      setKycError("Please complete all required personal KYC fields.");
      scrollToFormError();
      return;
    }

    const isUaeResident = personalForm.is_uae_resident === "true";
    const requiredDocs = requiredPersonalDocKeys(sh.shareholder_type, isUaeResident);
    const currentChecklist = checklistFor(sh, isUaeResident);
    const missingDocs = requiredDocs.filter((key) => {
      const item = currentChecklist.find((d) => d.document_key === key);
      const existingCount = item?.files?.length || 0;
      const pendingCount = pendingFiles[key]?.length || 0;
      return existingCount + pendingCount < 1;
    });
    if (missingDocs.length) {
      setKycError(
        `Upload required documents before saving: ${missingDocs.join(", ").replace(/_/g, " ")}.`,
      );
      scrollToFormError();
      return;
    }

    const name = personalForm.name.trim();
    setKycSaving(true);
    setKycError(null);
    try {
      const payload =
        sh.shareholder_type === "BUSINESS"
          ? { name, identity: personalForm.identity.trim() }
          : {
              name,
              nationality: Number(personalForm.nationality),
              passport_no: personalForm.passport_no.trim(),
              // The Emirates ID number is not collected; a UAE resident proves
              // it with the uploaded Emirates ID document.
              is_uae_resident: isUaeResident,
            };

      if (sh.personal_kyc) {
        await updatePersonalKyc(sh.personal_kyc.uuid, payload);
      } else {
        await createPersonalKyc(sh.uuid, payload);
      }

      // Refresh checklist against current docs so we upload with latest document_uuid.
      const [freshMappings, freshDocs] = await Promise.all([
        listDocumentMappings({ category: "personal_kyc" }),
        listApplicationDocuments(applicationUuid),
      ]);
      const checklist = buildPersonalChecklist(freshMappings, freshDocs, {
        shareholderId: sh.id,
        shareholderType: sh.shareholder_type,
        isUaeResident: sh.shareholder_type === "BUSINESS" ? false : isUaeResident,
      });
      const byKey = new Map(checklist.map((item) => [item.document_key, item]));

      for (const [docKey, files] of Object.entries(pendingFiles)) {
        const item = byKey.get(docKey);
        if (!item?.document_type_id) {
          throw new Error(`Missing document type for ${docKey}`);
        }
        let documentUuid = item.document_uuid ?? null;
        for (const file of files) {
          await uploadFileWithPasswordPrompt(file, (pdfPassword) =>
            uploadApplicationDocument(applicationUuid, {
              documentTypeId: item.document_type_id!,
              file,
              shareholderUuid: sh.uuid,
              documentUuid,
              pdfPassword,
            }),
          );
          if (!documentUuid) {
            const refreshed = await listApplicationDocuments(applicationUuid);
            documentUuid =
              refreshed.find(
                (d) => d.shareholder === sh.id && d.document_type === item.document_type_id,
              )?.uuid ?? null;
          }
        }
      }

      closeKycEdit();
      toast.success(sh.personal_kyc ? "Shareholder KYC saved." : "Shareholder KYC created.");
      await load();
      await onChanged?.();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Unable to save personal KYC";
      setKycError(message);
      toast.error(message);
      scrollToFormError();
    } finally {
      setKycSaving(false);
    }
  };

  const onDeleteExistingFile = async (item: DocumentChecklistItem, fileUuid: string) => {
    if (locked) return;
    if (!item.document_uuid) return;
    setKycError(null);
    try {
      await deleteApplicationDocumentFile(applicationUuid, {
        documentUuid: item.document_uuid,
        documentFileUuid: fileUuid,
      });
      await load();
      await onChanged?.();
    } catch (err) {
      setKycError(err instanceof ApiError ? err.message : "Unable to delete file");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-7" />
      </div>
    );
  }

  const { topLevel: topLevelShareholders, childrenByParent } = groupShareholders(shareholders);
  const topLevelTotal = poolTotal(topLevelShareholders);
  const topLevelFull = topLevelTotal >= 100;
  const formParent = formParentUuid
    ? (shareholders.find((s) => s.uuid === formParentUuid) ?? null)
    : null;
  const editingChildrenCount = editingUuid
    ? shareholders.filter((s) => s.business_shareholder === editingUuid).length
    : 0;
  const shareholderTypeLocked = Boolean(formParentUuid) || editingChildrenCount > 0;

  function renderCard(sh: Shareholder, indent: boolean) {
    return (
      <ShareholderCard
        key={sh.uuid}
        sh={sh}
        indent={indent}
        locked={locked}
        nationalities={nationalities}
        personalForm={personalForm}
        setPersonalForm={setPersonalForm}
        kycError={kycError}
        kycFieldErrors={kycFieldErrors}
        kycSaving={kycSaving}
        pendingFiles={pendingFiles}
        expandedDocKeys={expandedDocKeys}
        onToggleDocFiles={toggleDocFiles}
        onOpenDocPicker={openDocPicker}
        onRemovePendingFile={removePendingFile}
        onDeleteExistingFile={onDeleteExistingFile}
        expanded={expandedUuid === sh.uuid}
        editingKyc={editingKycUuid === sh.uuid}
        editChecklist={checklistFor(sh, personalForm.is_uae_resident === "true")}
        viewChecklist={checklistFor(sh)}
        onEdit={() => openEdit(sh)}
        onDelete={() => void onDelete(sh)}
        onToggleExpand={() => toggleExpand(sh.uuid)}
        onOpenKycEdit={() => openKycEdit(sh)}
        onCloseKycEdit={closeKycEdit}
        onSavePersonalKyc={(event) => void onSavePersonalKyc(sh, event)}
      />
    );
  }

  return (
    <>
      {renderPasswordModal()}
      <div className="space-y-5">
        <section className="rounded-2xl border border-line bg-surface-raised p-5 sm:p-6">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-ink">Shareholders KYC</h2>
              <p className="mt-1 text-sm text-muted">
                {locked
                  ? "Shareholders and shareholder KYC are read-only after credit has been approved."
                  : `Add shareholders (holding > 0, total ≤ 100%). Personal KYC is required for beneficial owners and anyone with more than ${UBO_SHAREHOLDING_THRESHOLD}% holding.`}
              </p>
            </div>
            {!locked ? (
              <Button
                size="sm"
                disabled={topLevelFull}
                title={topLevelFull ? "Total shareholding already reaches 100%." : undefined}
                onClick={() => openCreate()}
              >
                + Add shareholder
              </Button>
            ) : null}
          </div>

          {locked ? (
            <Alert className="mb-4" tone="success">
              Shareholders and shareholder KYC cannot be added, edited or removed after credit has
              been approved.
            </Alert>
          ) : null}

          {error && !showForm ? <Alert className="mb-4">{error}</Alert> : null}

          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            multiple
            onChange={onPendingFileChange}
          />

          {showForm && !locked
            ? createPortal(
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 px-4 py-6"
                  onClick={(e) => {
                    if (e.target === e.currentTarget && !saving) closeForm();
                  }}
                >
                  <div
                    role="dialog"
                    aria-modal="true"
                    className="w-full max-w-2xl rounded-2xl border border-line bg-surface-raised p-6 shadow-xl"
                  >
                    <h3 className="mb-4 text-sm font-semibold text-ink">
                      {editingUuid
                        ? "Edit shareholder"
                        : formParentUuid
                          ? `Add individual shareholder${formParent ? ` of ${formParent.name}` : ""}`
                          : "Add shareholder"}
                    </h3>
                    {error ? <Alert className="mb-4">{error}</Alert> : null}
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div>
                        <Select
                          label="Shareholder type *"
                          name="shareholder_type"
                          value={form.shareholder_type}
                          disabled={shareholderTypeLocked}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              shareholder_type: e.target.value as ShareholderType,
                            }))
                          }
                          options={[
                            { value: "INDIVIDUAL", label: "Individual" },
                            { value: "BUSINESS", label: "Business" },
                          ]}
                        />
                        {shareholderTypeLocked ? (
                          <p className="mt-1 text-xs text-muted">
                            {formParentUuid
                              ? "Individual shareholders of a company cannot be of type Business."
                              : "Cannot be changed while this company has individual shareholders."}
                          </p>
                        ) : null}
                      </div>
                      <Input
                        label="Name *"
                        name="name"
                        value={form.name}
                        onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                        error={fieldErrors.name}
                      />
                      <Input
                        label="Shareholding (%) *"
                        name="shareholding_percentage"
                        type="number"
                        min={0}
                        step="0.01"
                        value={form.shareholding_percentage}
                        onChange={(e) => onHoldingChange(e.target.value)}
                        error={fieldErrors.shareholding_percentage}
                      />
                    </div>
                    <label className="mt-4 flex items-center gap-2 text-sm text-ink">
                      <input
                        type="checkbox"
                        className="size-4 rounded border-line"
                        checked={form.is_business_owner}
                        disabled={Number(form.shareholding_percentage) > UBO_SHAREHOLDING_THRESHOLD}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, is_business_owner: e.target.checked }))
                        }
                      />
                      Yes, this shareholder is a beneficial owner
                    </label>
                    {fieldErrors.is_business_owner ? (
                      <p className="mt-1 text-xs text-danger">{fieldErrors.is_business_owner}</p>
                    ) : null}
                    <div className="mt-4 flex justify-end gap-2">
                      <Button type="button" variant="ghost" size="sm" onClick={closeForm}>
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        loading={saving}
                        onClick={() => void onSaveShareholder()}
                      >
                        {editingUuid ? "Update" : "Save"}
                      </Button>
                    </div>
                  </div>
                </div>,
                document.body,
              )
            : null}

          {shareholders.length === 0 ? (
            <p className="text-sm text-muted">No shareholders found.</p>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="rounded-xl border border-line bg-surface p-3">
                <CoverageBar label="Borrower company shareholding" total={topLevelTotal} />
              </div>

              <div className="flex flex-col gap-3">
                {topLevelShareholders.map((sh) => {
                  const children = childrenByParent.get(sh.uuid) ?? [];
                  const isBusiness = sh.shareholder_type === "BUSINESS";
                  const childTotal = poolTotal(children);
                  const childPoolFull = childTotal >= 100;

                  return (
                    <div key={sh.uuid} className="flex flex-col gap-3">
                      {renderCard(sh, false)}
                      {isBusiness ? (
                        <div className="ml-4 flex flex-col gap-3 rounded-xl border border-line bg-surface/60 p-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted">
                              Individual shareholders of {sh.name}
                            </p>
                            {!locked ? (
                              <Button
                                variant="secondary"
                                size="sm"
                                disabled={childPoolFull}
                                title={
                                  childPoolFull
                                    ? "Total shareholding already reaches 100%."
                                    : undefined
                                }
                                onClick={() => openCreate(sh.uuid)}
                              >
                                + Add individual shareholder
                              </Button>
                            ) : null}
                          </div>
                          <CoverageBar label={`Shareholders of ${sh.name}`} total={childTotal} />
                          {children.length === 0 ? (
                            <p className="text-sm text-muted">
                              No individual shareholders added yet.
                            </p>
                          ) : (
                            <div className="flex flex-col gap-2">
                              {children.map((child) => renderCard(child, true))}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>
    </>
  );
}
